import { useState } from "react";
import type { JSX } from "react";
import { Icon } from "../ui";
import { useSyncStatus } from "../../hooks/useSyncStatus";
import { useWebSocket } from "../../hooks/useWebSocket";
import { getCurrentWorkspace, useWorkspaceStore } from "../../store/workspaceStore";
import { TriggerLogPopover } from "./TriggerLogPopover";

function Divider(): JSX.Element {
  return <div className="w-px h-4 bg-edge shrink-0 mx-0.5" />;
}

/** Label mapping for known origin IDs. */
const ORIGIN_LABELS: Record<string, string> = {
  telegram: "Telegram",
  googleMail: "Gmail",
  google: "Gmail",
  emails: "Emails",
  search: "Search",
  files: "Files",
};

/**
 * Application status bar — rendered at the very bottom of the app shell.
 *
 * Consumes `useSyncStatus()` for real-time sync/index status.
 * No mocks, no polling — all push-based via WebSocket events.
 */
export function StatusBar(): JSX.Element {
  const { latestStatus, indexProgress } = useSyncStatus();
  const { backendReady } = useWebSocket();
  useWorkspaceStore((state) => state.selectedWorkspaceId);
  const currentWorkspace = getCurrentWorkspace();
  const [triggerPopoverOpen, setTriggerPopoverOpen] = useState(false);
  const workspaceLabel = currentWorkspace?.label ?? null;
  const connectionLabel = backendReady ? (workspaceLabel ?? "Connected") : "Connecting…";

  return (
    <div
      className="relative flex items-center justify-between h-8 px-2
        bg-surface-tertiary border-t border-edge shrink-0 text-[12px] text-content-secondary"
    >
      {/* Left section */}
      <div className="flex items-center gap-0.5 min-w-0 flex-1">

        {/* WS connection */}
        <div className="flex items-center gap-1 px-1.5 h-6 shrink-0">
          <span
            className={`w-1.5 h-1.5 rounded-full shrink-0 ${backendReady ? "bg-green-500" : "bg-yellow-500 animate-pulse"}`}
          />
          <span>{connectionLabel}</span>
          {/* @tested-by: tst_fe_status_bar_001
              @invariant: E2E readiness checks require a visible Connected label even when the workspace label is shown. */}
          {backendReady && workspaceLabel != null && (
            <>
              <span className="text-content-tertiary/60">·</span>
              <span>Connected</span>
            </>
          )}
        </div>

        {/* Latest status — one message, replaces previous */}
        {latestStatus != null && (
          <>
            <Divider />
            <div className="flex items-center gap-1.5 px-1.5 h-6 min-w-0">
              <span className="text-content-tertiary">
                {ORIGIN_LABELS[latestStatus.sourceId] ?? latestStatus.sourceId}:
              </span>
              <span className="truncate tabular-nums text-content-tertiary opacity-80">
                {latestStatus.message}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Right: index progress + triggers */}
      <div className="flex items-center gap-0.5 shrink-0">
        {/* Index progress — permanent indicator, like VS Code status */}
        {indexProgress != null && (
          <>
            <div className="flex items-center gap-1 px-1.5 h-6 text-content-tertiary tabular-nums">
              <Icon name="search" size={12} />
              <span>{indexProgress.indexed.toLocaleString()}/{indexProgress.total.toLocaleString()}</span>
            </div>
            <Divider />
          </>
        )}
        <div className="relative">
          <button
            type="button"
            onClick={() => { setTriggerPopoverOpen((v) => !v); }}
            className="flex items-center gap-1 px-1.5 h-6 rounded cursor-pointer
              hover:text-content hover:bg-surface-hover/50
              transition-colors duration-[120ms] border-none bg-transparent"
            title="Recent triggers"
          >
            <Icon name="bell" size={14} />
          </button>
          {triggerPopoverOpen && (
            <TriggerLogPopover onClose={() => { setTriggerPopoverOpen(false); }} />
          )}
        </div>
      </div>
    </div>
  );
}
